/* eslint-disable import/no-named-as-default */
import React, { useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'

import useDispatch from 'hooks/useDispatch'
import { SearchQueryResult } from '../hooks/useQueries'
import { disallowedFiltersForType } from '../actions/searchActions'
import { setListingsMobileFilterViewState } from '../actions/viewstateActions'

import useStickyNavbar from '../layout/Navbar/useStickyNavbar'
import LockBodyScroll from '../components/display/LockBodyScroll'
import FadeIn from '../components/display/FadeIn'
import FiltersIcon from '../components/icons/Filters'
import {
  Outerwrapper,
  MobileSearchViewWrapper,
  StickyMobileFilters,
  MobileFiltersOuter,
  MobileFiltersInner,
  ResultsWrapper,
  MapWrapper,
} from '../components/layout/ItemGridWithMap/Mobile'

import DatesFilter from './filters/mobile/DatesFilter'
import MultiSelectFilter from './filters/mobile/MultiSelectFilter'
import PeopleFilterPanel from './filters/mobile/PeopleFilter'
import MoreFilters from './filters/mobile/MoreFilters'
import MobileViewToggle from './filters/mobile/MobileViewToggle'

const FilterSpacer = styled.div`
  width: 8px;
  height: 16px;
`

type FilterButtonProps = {
  active?: boolean
}

export const MobileFilterButton = styled.button<FilterButtonProps>`
  display: flex;
  align-items: center;
  padding: 0.666em 0.8333em;

  font-size: 12px;

  border-radius: 6px;
  background-color: white;
  border: 1px solid #d5d5d5;

  svg {
    width: 13px;
    height: 11px;
    margin-right: 4px;
  }

  ${({ active, theme }) =>
    active &&
    `
    color: white;
    background-color: ${theme.brand.jungle};
    border: none;
  `}
`

const getDatesLabel = (filters: GQL_SearchFilters) => {
  const { startDate, endDate, weekendMonths } = filters
  if (weekendMonths?.length > 0 && startDate == null && endDate == null) {
    return 'Available weekends'
  }
  const formatDate = (d: string) => moment(d).format('DD MMM')
  const fomattedStart = startDate ? formatDate(startDate) : 'Start'
  const fomattedEnd = endDate ? formatDate(endDate) : 'End'
  return startDate || endDate ? `${fomattedStart} - ${fomattedEnd}` : 'Dates'
}

const getPeopleLabel = (filters: GQL_SearchFilters) => {
  const people = {
    adults: filters.adults || 0,
    children: filters.children || 0,
  }
  const numberOfPeople = Object.values(people).reduce((a, b) => a + b, 0)
  if (numberOfPeople > 0) {
    return `${numberOfPeople} ${numberOfPeople > 1 ? 'People' : 'Person'}`
  }
  return 'People'
}

type DisallowedFilters = {
  [key in keyof GQL_SearchFilters]: true
}

interface SearchViewProps {
  filters: GQL_SearchFilters
  defaults: SearchQueryResult['data']['search']['defaults']
  minPrice: number
  maxPrice: number
  resultsComponent: React.ReactNode
  mapComponent: React.ReactNode
  searchTypeToggleComponent: React.ReactNode
  resultsCount?: number
}

const MobileSearchView = ({
  filters,
  defaults,
  minPrice,
  maxPrice,
  resultsComponent,
  mapComponent,
  searchTypeToggleComponent,
  resultsCount,
}: SearchViewProps) => {
  const dispatch = useDispatch()
  const [hasToggledView, setHasToggledView] = useState(false)
  const [showMap, setShowMap] = useState(false)
  const [openPanel, setOpenPanel] = useState(null)

  useStickyNavbar(true)

  const handleOpenRegionsFilter = () => setOpenPanel('regions')
  const handleOpenDatesFilter = () => setOpenPanel('dates')
  const handleOpenPeopleFilter = () => setOpenPanel('people')
  const handleOpenAdventureTypesFilter = () => setOpenPanel('adventureTypes')
  const handleOpenMoreFilters = () => setOpenPanel('more')

  const handleClosePanel = () => setOpenPanel(null)

  const handleToggleView = () => {
    setHasToggledView(true)
    setShowMap(!showMap)
    dispatch(setListingsMobileFilterViewState(false))
  }

  const currentSearchType = filters.type
  const disallowedFiltersKeys: string[] =
    disallowedFiltersForType[currentSearchType] ?? []
  const disallowedFiltersDict = disallowedFiltersKeys.reduce(
    (acc, filterKey) => {
      return {
        ...acc,
        [filterKey]: true,
      }
    },
    {} as DisallowedFilters
  )

  const regionsFilterAsPrimary =
    currentSearchType === 'adventures' &&
    filters?.country != null &&
    filters?.state != null

  /**
   * We need to count filters that are active but group them by how they are
   * represented visually
   */
  const activeSecondaryFilters = [
    regionsFilterAsPrimary !== true && filters.regions,
    filters.priceStart || filters.priceEnd,
    filters.subcategories,
    filters.amenities,
    filters.petFriendly,
    filters.bedrooms,
    filters.beds,
    filters.instantBook,
    filters.fullRefund,
    filters.oneNightStayWeekday,
    filters.oneNightStayWeekend,
  ].filter((v) => !!v)

  const dateFilterAllowed =
    !disallowedFiltersDict.startDate && !disallowedFiltersDict.endDate
  const peopleFilterAllowed = !disallowedFiltersDict.adults

  const filtersBar = (
    <MobileFiltersOuter>
      <MobileFiltersInner>
        {searchTypeToggleComponent}
        {regionsFilterAsPrimary && (
          <MobileFilterButton
            onClick={handleOpenRegionsFilter}
            active={!!filters.regions}
          >
            Regions
          </MobileFilterButton>
        )}
        {dateFilterAllowed && (
          <MobileFilterButton
            onClick={handleOpenDatesFilter}
            active={
              filters.startDate || filters.endDate || filters.weekendMonths
            }
          >
            {getDatesLabel(filters)}
          </MobileFilterButton>
        )}
        {peopleFilterAllowed && (
          <MobileFilterButton
            onClick={handleOpenPeopleFilter}
            active={!!filters.adults || !!filters.children}
            data-testid="people-filter"
          >
            {getPeopleLabel(filters)}
          </MobileFilterButton>
        )}
        {currentSearchType === 'adventures' && (
          <MobileFilterButton
            onClick={handleOpenAdventureTypesFilter}
            active={!!filters.adventureTypes}
          >
            Adventure Type
          </MobileFilterButton>
        )}
        {currentSearchType !== 'adventures' && (
          <MobileFilterButton
            onClick={handleOpenMoreFilters}
            active={activeSecondaryFilters.length > 0}
            data-testid="more-filters"
          >
            <FiltersIcon />
            Filters
            {activeSecondaryFilters.length > 0 &&
              ` (${activeSecondaryFilters.length})`}
          </MobileFilterButton>
        )}
        <FilterSpacer />
      </MobileFiltersInner>
    </MobileFiltersOuter>
  )

  return (
    <Outerwrapper>
      <MobileSearchViewWrapper>
        {(showMap || openPanel) && <LockBodyScroll />}

        {showMap ? (
          // placeholder to avoid layout shift; hardcoded to the effective height of filters bar for now
          <div style={{ height: '65px', boxSizing: 'border-box' }} />
        ) : (
          <StickyMobileFilters>{filtersBar}</StickyMobileFilters>
        )}

        <ResultsWrapper
          className="js-global-loader-container"
          style={{ visibility: showMap ? 'hidden' : 'visible' }}
        >
          {resultsComponent}
        </ResultsWrapper>

        {hasToggledView && (
          <MapWrapper
            $offsetForNavbar
            style={{
              display: 'flex',
              flexDirection: 'column',
              visibility: showMap ? 'visible' : 'hidden',
            }}
          >
            {showMap && filtersBar}
            <FadeIn
              $show={showMap}
              style={{ flexGrow: 1, height: '100%', width: '100%' }}
            >
              {mapComponent}
            </FadeIn>
          </MapWrapper>
        )}

        <MobileViewToggle mapOpen={showMap} onClick={handleToggleView} />
      </MobileSearchViewWrapper>

      <DatesFilter
        filters={filters}
        open={openPanel === 'dates'}
        onClose={handleClosePanel}
        resultsCount={resultsCount}
      />

      <PeopleFilterPanel
        filters={filters}
        open={openPanel === 'people'}
        onClose={handleClosePanel}
        resultsCount={resultsCount}
      />

      <MultiSelectFilter
        heading="Adventure Type"
        filterKey="adventureTypes"
        open={openPanel === 'adventureTypes'}
        onClose={handleClosePanel}
        possibleFilters={defaults?.adventureTypes}
        selectedFiltersSlugs={filters?.adventureTypes}
        resultsCount={resultsCount}
        noTruncate
      />

      <MultiSelectFilter
        heading="Regions"
        filterKey="regions"
        open={openPanel === 'regions'}
        onClose={handleClosePanel}
        possibleFilters={defaults?.regions}
        selectedFiltersSlugs={filters?.regions}
        resultsCount={resultsCount}
        noTruncate
      />

      <MoreFilters
        includeRegionsFilter={regionsFilterAsPrimary !== true}
        filters={filters}
        defaults={defaults}
        maxPrice={maxPrice}
        minPrice={minPrice}
        open={openPanel === 'more'}
        resultsCount={resultsCount}
        onClose={handleClosePanel}
      />
    </Outerwrapper>
  )
}

export default MobileSearchView
